import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { HygienistBackendJob } from "components/EditJob";

import {
  JobContainer,
  TitlesContainer,
  Title,
  Subtitle,
  NewCandidatesInfo,
  PersonOutlineIconStyled,
  JobInfoContainer,
  JobInfoItem,
  CalendarTodayOutlinedIconStyled,
  StartingDate,
  PostedOnText,
  Badge,
  StyledEliteImage,
  StyledProImage,
  FeaturedBadge
} from './JobList.styles'

dayjs.extend(utc);

interface JobListItemProps {
  job: HygienistBackendJob,
  onJobClick: (job: HygienistBackendJob) => void
}

const formatShortDate = (dateString: string | undefined) => {
  if (!dateString) return '';
  const date = dayjs.utc(dateString);
  if (!date.isValid()) return '';
  return date.format('MMM, DD/YY');
};

// The server stores Job.startDate as a date-only string (YYYY-MM-DD). Parsing
// it through Date/dayjs and comparing to `now()` flips the result across
// timezone boundaries: with dayjs.utc() the local evening in NA crosses into
// the next UTC day and "tomorrow" renders as "Immediately" hours before the
// local date actually rolls over, and with local dayjs() a YYYY-MM-DD string
// is interpreted as local midnight so the hygienist can see the previous day.
// Extracting YYYY-MM-DD and comparing against the user's local-day string
// avoids both edge cases.
const getYmd = (dateString: string | undefined): string => {
  if (!dateString) return '';
  const slice = String(dateString).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(slice) ? slice : '';
};
const isStartDateImmediateOrPast = (startDate?: string): boolean => {
  const startYmd = getYmd(startDate);
  if (!startYmd) return false;
  const todayYmd = dayjs().format('YYYY-MM-DD');
  return startYmd <= todayYmd;
};

const HygienistJobListItem = ({ job, onJobClick }: JobListItemProps) => {
  const {
    title,
    id,
    location,
    isApplied,
    startDate,
    createdOn
  } = job;

  const NewCandidatesBadge = isApplied ? (
    <NewCandidatesInfo>
      <PersonOutlineIconStyled />
      <span>Applied</span>
    </NewCandidatesInfo>
  ) : null;

  const formattedPostedOn = formatShortDate(createdOn);

  const isFeatured = (job as any)?.isFeatured;
  const isFeatureDisplayed = (job as any)?.isFeatureDisplayed;

  const containerClass = isFeatureDisplayed ? 'no-fastclick featuredDisplayed' : 'no-fastclick';

  return (
    <JobContainer
      key={id}
      onClick={() => onJobClick(job)}
      className={containerClass}
      id={`hygienist-job-card-${id}`}
      data-testid={`hygienist-job-card-${id}`}
      aria-label={`hygienist-job-card-${id}`}
      role="article"
      content-desc={`hygienist-job-card-${id}`}
    >
      <TitlesContainer>
        <div>
          <Title
            id={`hygienist-job-title-${id}`}
            data-testid={`hygienist-job-title-${id}`}
            aria-label={`hygienist-job-title-${id}`}
            content-desc={`hygienist-job-title-${id}`}
          >{title}</Title>
          <Subtitle>
            <span
              id={`hygienist-job-location-${id}`}
              data-testid={`hygienist-job-location-${id}`}
              aria-label={`hygienist-job-location-${id}`}
              content-desc={`hygienist-job-location-${id}`}
            >{[location?.address, location?.city, location?.province].filter(Boolean).join(', ')}</span>
            <Badge style={{ display: 'inline-flex', alignItems: 'center' }}>
              {location?.status === 1 && <StyledProImage />}
              {location?.status === 2 && <StyledEliteImage />}
              {/* Featured indicator: bold badge when currently displayed; subtle when only featured */}
              {isFeatureDisplayed && (
                <FeaturedBadge>
                  ⭐ Featured
                </FeaturedBadge>
              )}
              {isFeatured && !isFeatureDisplayed && (
                <span style={{ marginLeft: 8, color: 'var(--primary-blue, #0B4598)', fontWeight: 600 }}>⭐ Featured</span>
              )}
            </Badge>
          </Subtitle>
        </div>
        <div className="applied-badge-wrapper">{NewCandidatesBadge}</div>
      </TitlesContainer>
      <JobInfoContainer>
        <JobInfoItem>
          <CalendarTodayOutlinedIconStyled />
          <b>Starting:</b>
          <StartingDate $isHygienist={true}
            id={`hygienist-job-startdate-${id}`}
            data-testid={`hygienist-job-startdate-${id}`}
            aria-label={`hygienist-job-startdate-${id}`}
            content-desc={`hygienist-job-startdate-${id}`}
          >
            {startDate ? (isStartDateImmediateOrPast(startDate) ? ' Immediately' : ` ${formatShortDate(startDate)}`) : ''}
          </StartingDate>
        </JobInfoItem>
        {formattedPostedOn && (
            <JobInfoItem>
                <CalendarTodayOutlinedIconStyled />
                <b>Posted On:</b>
                <PostedOnText
                  id={`hygienist-job-postedon-${id}`}
                  data-testid={`hygienist-job-postedon-${id}`}
                  aria-label={`hygienist-job-postedon-${id}`}
                  content-desc={`hygienist-job-postedon-${id}`}
                >{formattedPostedOn}</PostedOnText>
            </JobInfoItem>
        )}
      </JobInfoContainer>
    </JobContainer>
  )
}

export default HygienistJobListItem;
